import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Divider } from '@mic3/platform-ui';

import Loader from 'app/components/atoms/Loader/Loader';
import { get } from 'app/utils/lo/lo';
import {
    ExpansionPanelDetailsStyled,
    getAvatarUrl,
    ListItemChildStyled,
    StyledAvatarChild,
    getSubTitle as getSubTitleUtil,
} from './selectionListUtils';
import TypeIcon from 'app/components/atoms/TypeIcon/TypeIcon';
import { CardDivider } from 'app/containers/Entities/EntitiesList/EntitiesListItem';
import RolesSelect from 'app/components/molecules/RolesSelect/RolesSelect';
import { loadTeamsWorkspaces } from 'store/actions/admin/teamsActions';
import VirtualList from 'app/components/molecules/VirtualList/VirtualList';

const TeamWorkspacesList = ({ teamId, expanded, type, typeChild, showChildRoles, noSubTitle, expansionDetailCaption, refreshList }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadTeamWorkspaces = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await loadTeamsWorkspaces(teamId);
            const workspacesData = get(response, 'data.records', []);
            setWorkspaces(workspacesData);
            refreshList?.();
        } catch (error) {}
        setIsLoading(false);

    }, [teamId, refreshList]);

    useEffect(() => {
        loadTeamWorkspaces();
    }, [loadTeamWorkspaces]);

   
    const getSubTitle = useMemo(() => {
        return (data) => getSubTitleUtil(type, data);
    }, [type]);

    const getRoleByTeamId = useCallback((data) => {
        const { teams } = data;

        if (!teams || !Array.isArray(teams)) {
            return null;
        }

        const matchingTeam = teams.find(teamItem =>
            get(teamItem, 'team.id') === teamId
        );

        return get(matchingTeam, 'role', null);
    }, [teamId]);

    const renderWorkspace = useCallback(({ style, data }) => {
        const { id, name, image } = data;
        const avatar = getAvatarUrl(type, id, image);
        const workspaceRole = getRoleByTeamId(data);

        return (
            <div style={style} key={id}>
                <ListItemChildStyled
                    component={<StyledAvatarChild src={avatar} initials={name} size='lg' />}
                    title={
                        <>
                            <TypeIcon type={typeChild} size='sm' />
                            <CardDivider flexItem orientation='vertical' />
                            {get(data, 'name')}
                        </>
                    }
                    subTitle={noSubTitle ? '' : getSubTitle(data)}
                    actions={<>{showChildRoles && <RolesSelect disabled defaultValue={workspaceRole} />}</>}
                    raised
                />
            </div>
        );
    }, [type, typeChild, showChildRoles, noSubTitle, getSubTitle, getRoleByTeamId]);

    const itemSize = 80;
    const itemsCount = workspaces?.length || 0;

    return (
        <ExpansionPanelDetailsStyled itemSize={itemSize} itemCount={itemsCount}>
            <Divider />
            {!isLoading && itemsCount < 1 && (
                <div
                    style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#666'
                    }}
                >
                    No Results
                </div>
            )}

            {isLoading && <Loader absolute />}

            {!isLoading && itemsCount > 0 && (
                <>
                    {expansionDetailCaption}
                    <VirtualList
                        itemSize={itemSize}
                        itemCount={itemsCount}
                        width="100%"
                        renderItem={({ index, style }) => {
                            return workspaces[index] ? (
                                renderWorkspace({
                                    style,
                                    data: workspaces[index]
                                })
                            ) : (
                                <div style={style} key={index} />
                            );
                        }}
                    />
                </>
            )}
        </ExpansionPanelDetailsStyled>
    );
};

TeamWorkspacesList.propTypes = {
    teamId: PropTypes.string.isRequired,
    expanded: PropTypes.bool,
    type: PropTypes.string,
    typeChild: PropTypes.string,
    showChildRoles: PropTypes.bool,
    noSubTitle: PropTypes.bool,
    expansionDetailCaption: PropTypes.node,
    refreshList: PropTypes.func,
};

export default TeamWorkspacesList;
